import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, tokenManager } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = tokenManager.getToken();
      
      if (!token || tokenManager.isTokenExpired()) {
        setIsAuthenticated(false);
        setUser(null);
        tokenManager.removeToken();
        return;
      }

      // Verify token with server
      const response = await authAPI.verify();
      setIsAuthenticated(true);
      setUser(response.data.user);
    } catch (error) {
      console.error('Auth check failed:', error);
      setIsAuthenticated(false);
      setUser(null);
      tokenManager.removeToken();
    } finally {
      setLoading(false);
    }
  };

  const login = async (password) => {
    try {
      const response = await authAPI.login(password);
      const { token } = response.data;
      
      tokenManager.setToken(token);
      setIsAuthenticated(true);
      
      // Get user info after login
      const userResponse = await authAPI.verify();
      setUser(userResponse.data.user);
      
      // Navigate to home page after successful login
      navigate('/', { replace: true });
      
      return response.data;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      tokenManager.removeToken();
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};